import type { UIMessage } from "@tanstack/ai-react";

import { withGatekeeperMetadata } from "@/lib/qualification-lifecycle/metadata-part";
import {
  LEGACY_PROJECTED_ASSISTANT_ANSWER_ID,
  projectedAssistantAnswerId,
  projectedUserQuestionId,
} from "@/lib/qualification-lifecycle/projected-message-ids";
import { buildRejectedTransientPrecision } from "@/lib/qualification-lifecycle/rejected-transient-precision";
import { isGatekeeperStreamingPlaceholder } from "@/lib/qualification-lifecycle/serialize-answers";
import type {
  QualificationLifecycleState,
  QualificationTurn,
} from "@/lib/qualification-lifecycle/types";
import type { GatekeeperPrecisionPair } from "@/types";
import { withCancelledStatus } from "@/lib/ui-helper/messages";

export function remapAttemptProjectedIdsInUiMessages(
  messages: UIMessage[],
  attemptId: string,
  dustUserMessageId?: string | null,
  dustAssistantMessageId?: string | null
): UIMessage[] {
  const userProjectedId = projectedUserQuestionId(attemptId);
  const assistantProjectedId = projectedAssistantAnswerId(attemptId);

  return messages.map((msg) => {
    let nextId = msg.id;
    if (dustUserMessageId && nextId === userProjectedId) {
      nextId = dustUserMessageId;
    }
    if (dustAssistantMessageId && nextId === assistantProjectedId) {
      nextId = dustAssistantMessageId;
    }
    return nextId === msg.id ? msg : { ...msg, id: nextId };
  });
}

export function resolveStreamingAssistantMessageId(
  messages: UIMessage[],
  attemptId: string | null
): string | null {
  const candidates: string[] = [];
  if (attemptId) {
    candidates.push(projectedAssistantAnswerId(attemptId));
  }
  candidates.push(LEGACY_PROJECTED_ASSISTANT_ANSWER_ID);

  for (const id of candidates) {
    const match = messages.find((m) => m.role === "assistant" && m.id === id);
    if (match) return match.id;
  }

  return messages.findLast((m) => m.role === "assistant")?.id ?? null;
}

/** @deprecated Use attempt-scoped ids via projectedUserQuestionId. */
const PROJECTED_MESSAGE_IDS = {
  userQuestion: "qualification-user-question",
  assistantUnavailable: "qualification-assistant-unavailable",
  assistantAnswer: "qualification-assistant-answer",
} as const;

function resolveProjectedUserQuestionId(
  state: QualificationLifecycleState
): string {
  return state.attemptId
    ? projectedUserQuestionId(state.attemptId)
    : PROJECTED_MESSAGE_IDS.userQuestion;
}

function resolveProjectedAssistantAnswerId(
  state: QualificationLifecycleState
): string {
  return state.attemptId
    ? projectedAssistantAnswerId(state.attemptId)
    : PROJECTED_MESSAGE_IDS.assistantAnswer;
}

function questionBubbleCreatedAt(state: QualificationLifecycleState): Date {
  return state.questionSentAt ?? new Date();
}

function gatekeeperAssistantCreatedAt(
  state: QualificationLifecycleState
): Date {
  return state.gatekeeperTerminalAt ?? state.questionSentAt ?? new Date();
}

function answeringAssistantCreatedAt(state: QualificationLifecycleState): Date {
  return state.assistantAnswerStartedAt ?? state.questionSentAt ?? new Date();
}

/** Liste de précisions brute depuis le reducer (source de vérité métier). */
export function precisionsFromLifecycleState(
  state: QualificationLifecycleState
): GatekeeperPrecisionPair[] {
  const fromTurns: GatekeeperPrecisionPair[] = state.turns.map((turn) => ({
    clarification: turn.clarification,
    clarificationQuestions: turn.clarificationQuestions,
    userReply: turn.userReply,
    structuredReply: turn.structuredReply,
    transmissionPending: turn.transmissionPending,
  }));
  const base = [...state.foldedPrecisions, ...fromTurns];
  const rejected = buildRejectedTransientPrecision(state);
  return rejected ? [...base, rejected] : base;
}

/** Paires persistables — dérivées du reducer, pas du fil TanStack AI. */
export function persistablePrecisionsFromLifecycleState(
  state: QualificationLifecycleState
): GatekeeperPrecisionPair[] {
  return persistableGatekeeperPrecisions(precisionsFromLifecycleState(state));
}

function resolveGatekeeperPhase(
  state: QualificationLifecycleState
): "clarifying" | "qualified" | undefined {
  if (state.phase === "qualified" || state.phase === "answering") {
    return state.qualifiedQuestionText ? "qualified" : "clarifying";
  }
  if (
    state.phase === "clarifying" ||
    state.phase === "qualifying" ||
    state.phase === "qualification_unavailable" ||
    state.phase === "quota_interrupted" ||
    state.phase === "attempt_abandoned"
  ) {
    return precisionsFromLifecycleState(state).length > 0
      ? "clarifying"
      : undefined;
  }
  return undefined;
}

function buildQuestionBubble(state: QualificationLifecycleState): UIMessage {
  const content =
    state.qualifiedQuestionText?.trim() || state.questionText.trim() || " ";
  const precisions = precisionsFromLifecycleState(state);
  const phase = resolveGatekeeperPhase(state);

  return withGatekeeperMetadata(
    {
      id: resolveProjectedUserQuestionId(state),
      role: "user",
      parts: [{ type: "text", content }],
      createdAt: questionBubbleCreatedAt(state),
    },
    {
      userIntent: "question",
      gatekeeperPrecisions: precisions.length ? precisions : undefined,
      gatekeeperPhase: phase,
    }
  );
}

function buildUnavailableAssistantBubble(
  state: QualificationLifecycleState,
  message: string
): UIMessage {
  return {
    id: PROJECTED_MESSAGE_IDS.assistantUnavailable,
    role: "assistant",
    parts: [{ type: "text", content: message }],
    createdAt: gatekeeperAssistantCreatedAt(state),
  };
}

function buildAnsweringAssistantBubble(
  state: QualificationLifecycleState,
  text: string
): UIMessage {
  const base: UIMessage = {
    id: resolveProjectedAssistantAnswerId(state),
    role: "assistant",
    parts: [{ type: "text", content: text }],
    createdAt: answeringAssistantCreatedAt(state),
  };
  return state.answerGenerationCancelled ? withCancelledStatus(base) : base;
}

/**
 * Projects qualification lifecycle state onto UIMessages appended after prefix.
 */
export function projectQualificationMessages(
  prefix: UIMessage[],
  state: QualificationLifecycleState
): UIMessage[] {
  if (state.phase === "idle" || state.phase === "stabilized") {
    return prefix;
  }

  const attemptMessages: UIMessage[] = [];

  if (state.phase === "rejected_transient" && state.rejectionText) {
    attemptMessages.push(buildQuestionBubble(state));
    return [...prefix, ...attemptMessages];
  }

  if (state.phase === "qualification_unavailable" && state.unavailableMessage) {
    attemptMessages.push(buildQuestionBubble(state));
    attemptMessages.push(
      buildUnavailableAssistantBubble(state, state.unavailableMessage)
    );
    return [...prefix, ...attemptMessages];
  }

  if (state.phase === "quota_interrupted") {
    attemptMessages.push(buildQuestionBubble(state));
    return [...prefix, ...attemptMessages];
  }

  if (state.phase === "attempt_abandoned") {
    attemptMessages.push(buildQuestionBubble(state));
    return [...prefix, ...attemptMessages];
  }

  attemptMessages.push(buildQuestionBubble(state));

  if (
    state.phase === "answering" ||
    state.phase === "qualified" ||
    state.passedThisRun
  ) {
    const answerText = state.answeringAssistantText;
    if (
      answerText ||
      state.answerGenerationCancelled ||
      state.phase === "answering" ||
      (state.passedThisRun && state.phase === "qualified")
    ) {
      attemptMessages.push(buildAnsweringAssistantBubble(state, answerText));
    }
  }

  return [...prefix, ...attemptMessages];
}

export function persistableGatekeeperPrecisions(
  precisions: GatekeeperPrecisionPair[] | undefined
): GatekeeperPrecisionPair[] {
  return (precisions ?? []).filter((pair) => {
    if (pair.transmissionPending) return false;
    if (pair.outcome === "rejected") {
      return Boolean(pair.userReply?.trim() && pair.clarification?.trim());
    }
    return Boolean(pair.userReply?.trim());
  });
}

export function isStreamingClarificationTurn(turn: QualificationTurn): boolean {
  return isGatekeeperStreamingPlaceholder(turn.clarification);
}
